import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2, RefreshCw, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { GenerationLoadingState, OnboardingStepSkeleton } from "@/components/loading";
import type { GenerationStage } from "@/components/loading";
import { OnboardingHeader } from "@/components/patterns/headers";
import { Button } from "@/components/ui/button";
import { LoadingButton } from "@/components/ui/custom/loading-button";
import { Input } from "@/components/ui/input";
import { useSession } from "@/hooks/use-session";
import { useSupabaseBroadcast } from "@/hooks/use-supabase-broadcast";
import {
      getInitialPromptGenerationViewState,
      ONBOARDING_GENERATION_TIMEOUT_MS,
      shouldAutoStartInitialPromptGeneration,
} from "@/lib/onboarding-generation";
import {
      hasCompletedBrandOnboarding,
      markBrandOnboardingCompleteInCache,
} from "@/lib/onboarding-progress";
import { getPromptEditorAutofillProps } from "@/lib/prompt-input";
import { useTRPC } from "@/lib/trpc";
import { useBrandStore } from "@/state/store/brand";
import { useSearchSpecificationStore } from "@/state/store/searchSpecification";

export const Route = createFileRoute("/onboarding/brand/$brandId/prompts")({
      component: RouteComponent,
      validateSearch: (search: Record<string, unknown>): { searchSpecificationId?: string } => ({
            searchSpecificationId:
                  typeof search.searchSpecificationId === "string" ? search.searchSpecificationId : undefined,
      }),
});

const PROMPT_GENERATION_STAGES: GenerationStage[] = [
      { id: "GENERATING", label: "Generating", progressRange: [5, 90] },
];

const PROMPT_TIPS = [
      "Prompts are crafted from your persona and brand profile to mirror real customer searches.",
      "You can edit any generated prompt before starting your first search.",
      "Upsearch tests these prompts across multiple AI platforms to measure your brand's visibility.",
      "After onboarding, you'll be able to add competitor and brand-specific prompts too.",
];

type PromptGenerationMode = "STANDARD" | "ONBOARDING";
type PromptGenerationBroadcastStage = "GENERATING" | "COMPLETED" | "FAILED";
type PromptGenerationBroadcastPayload = {
      generationId?: string;
      searchSpecificationId?: string;
      mode?: PromptGenerationMode;
      stage?: PromptGenerationBroadcastStage;
      prompts?: Array<{
            text: string;
            promptType: "ORGANIC" | "COMPETITOR" | "BRAND_SPECIFIC";
      }>;
      error?: string;
};

type PromptDraft = {
      id: string;
      text: string;
      promptType: "ORGANIC";
};

const ONBOARDING_STARTER_PROMPT_LIMIT = 5;
const trimSafe = (value: string | null | undefined): string => (value ?? "").trim();

function RouteComponent() {
      const { brandId } = Route.useParams();
      const { searchSpecificationId } = Route.useSearch();
      const trpc = useTRPC();
      const queryClient = useQueryClient();
      const navigate = useNavigate();
      const { sessionData, isLoading: isLoadingSession } = useSession();

      const [isSubmitting, setIsSubmitting] = useState(false);

      const setSelectedBrandId = useBrandStore((state) => state.setSelectedBrandId);
      const setCurrentSearchSpecificationId = useSearchSpecificationStore(
            (state) => state.setCurrentSearchSpecificationId,
      );

      const [promptDrafts, setPromptDrafts] = useState<PromptDraft[]>([]);
      const [regeneratingPromptIds, setRegeneratingPromptIds] = useState<Set<string>>(new Set());
      const [initialPromptGenerationId, setInitialPromptGenerationId] = useState<string | null>(null);
      const initialPromptGenerationIdRef = useRef<string | null>(null);
      const initialPromptGenerationTimeoutRef = useRef<number | null>(null);
      const promptGenerationToPromptIdRef = useRef<Map<string, string>>(new Map());
      const promptGenerationTimeoutsRef = useRef<Map<string, number>>(new Map());
      const [hasTriggeredInitialGeneration, setHasTriggeredInitialGeneration] = useState(false);
      const [initialPromptGenerationError, setInitialPromptGenerationError] = useState<string | null>(null);
      const [promptGenerationBroadcastStage, setPromptGenerationBroadcastStage] = useState<string | null>(null);

      const { data: brandBeingCreated, isLoading: isLoadingBrand } = useQuery(
            trpc.brands.getBrand.queryOptions({ id: brandId }, { enabled: !!sessionData && !!brandId }),
      );
      const { data: brandSearchExecutionCount, isLoading: isLoadingBrandSearchExecutionCount } = useQuery(
            trpc.brands.getBrandSearchExecutionsCount.queryOptions(
                  { brandId },
                  { enabled: !!sessionData && !!brandId },
            ),
      );

      const generateOnboardingPromptsMutation = useMutation(
            trpc.prompts.generateOnboardingPrompts.mutationOptions({}),
      );
      const createPromptsMutation = useMutation(trpc.prompts.createPrompts.mutationOptions({}));
      const startSearchMutation = useMutation(trpc.searches.startSearch.mutationOptions({}));

      useEffect(() => {
            if (!sessionData && !isLoadingSession) {
                  navigate({ to: "/auth", replace: true });
            }
      }, [sessionData, isLoadingSession, navigate]);

      useEffect(() => {
            if (!isLoadingBrand && sessionData && !brandBeingCreated) {
                  toast.error("Brand not found or you do not have access.");
                  navigate({ to: "/onboarding/brand/new", replace: true });
            }
      }, [isLoadingBrand, sessionData, brandBeingCreated, navigate]);

      useEffect(() => {
            if (!brandBeingCreated?.slug || isLoadingBrandSearchExecutionCount) return;
            if (!hasCompletedBrandOnboarding(brandSearchExecutionCount)) return;

            setSelectedBrandId(brandBeingCreated.id);
            setCurrentSearchSpecificationId(null);
            navigate({
                  to: "/$brand/dashboard",
                  params: { brand: brandBeingCreated.slug },
                  replace: true,
            });
      }, [
            brandBeingCreated?.id,
            brandBeingCreated?.slug,
            brandSearchExecutionCount,
            isLoadingBrandSearchExecutionCount,
            navigate,
            setCurrentSearchSpecificationId,
            setSelectedBrandId,
      ]);

      const {
            data: existingPromptsPage,
            isLoading: isLoadingExistingPrompts,
            isFetched: hasFetchedExistingPrompts,
      } = useQuery({
            ...trpc.prompts.getSearchSpecificationPrompts.queryOptions({
                  searchSpecificationId: searchSpecificationId || "",
            }),
            enabled: !!sessionData && !!searchSpecificationId,
      });

      const existingPromptItems = useMemo(
            () => existingPromptsPage?.items ?? [],
            [existingPromptsPage?.items],
      );

      const clearInitialPromptGeneration = useCallback(() => {
            initialPromptGenerationIdRef.current = null;
            setInitialPromptGenerationId(null);
            if (initialPromptGenerationTimeoutRef.current !== null) {
                  window.clearTimeout(initialPromptGenerationTimeoutRef.current);
                  initialPromptGenerationTimeoutRef.current = null;
            }
      }, []);

      const clearRegenerationTracking = useCallback((generationId: string) => {
            const timeoutId = promptGenerationTimeoutsRef.current.get(generationId);
            if (timeoutId !== undefined) {
                  window.clearTimeout(timeoutId);
                  promptGenerationTimeoutsRef.current.delete(generationId);
            }

            const promptId = promptGenerationToPromptIdRef.current.get(generationId);
            if (!promptId) return null;

            promptGenerationToPromptIdRef.current.delete(generationId);
            setRegeneratingPromptIds((prev) => {
                  const next = new Set(prev);
                  next.delete(promptId);
                  return next;
            });

            return promptId;
      }, []);

      const scheduleInitialPromptGenerationTimeout = useCallback(
            (generationId: string) => {
                  if (initialPromptGenerationTimeoutRef.current !== null) {
                        window.clearTimeout(initialPromptGenerationTimeoutRef.current);
                  }

                  initialPromptGenerationTimeoutRef.current = window.setTimeout(() => {
                        if (initialPromptGenerationIdRef.current !== generationId) return;
                        clearInitialPromptGeneration();
                        setInitialPromptGenerationError("Prompt generation timed out. Try again.");
                        toast.error("Prompt generation timed out. Try again.");
                  }, ONBOARDING_GENERATION_TIMEOUT_MS);
            },
            [clearInitialPromptGeneration],
      );

      const scheduleRegenerationTimeout = useCallback(
            (generationId: string, promptId: string) => {
                  const timeoutId = window.setTimeout(() => {
                        if (promptGenerationToPromptIdRef.current.get(generationId) !== promptId) return;
                        clearRegenerationTracking(generationId);
                        toast.error("Prompt regeneration timed out. Try again.");
                  }, ONBOARDING_GENERATION_TIMEOUT_MS);

                  promptGenerationTimeoutsRef.current.set(generationId, timeoutId);
            },
            [clearRegenerationTracking],
      );

      const promptBroadcast = useSupabaseBroadcast({
            channelName: `prompt_generation:${searchSpecificationId ?? "unknown"}`,
            event: "prompt_generation_progress",
            callbackFn: (broadcastMessage: { payload?: PromptGenerationBroadcastPayload }) => {
                  const payload = broadcastMessage.payload;
                  if (!payload?.generationId || !payload.searchSpecificationId) return;
                  if (!searchSpecificationId || payload.searchSpecificationId !== searchSpecificationId) return;

                  if (payload.generationId === initialPromptGenerationIdRef.current) {
                        if (payload.stage === "GENERATING") {
                              setPromptGenerationBroadcastStage("GENERATING");
                              return;
                        }

                        if (payload.stage === "COMPLETED") {
                              const generatedPrompts = payload.prompts ?? [];
                              if (generatedPrompts.length === 0) {
                                    setInitialPromptGenerationError("Prompt generation returned no prompts.");
                                    toast.error("Prompt generation returned no prompts");
                              } else {
                                    setPromptDrafts(
                                          generatedPrompts.map((prompt) => ({
                                                id: crypto.randomUUID(),
                                                text: prompt.text,
                                                promptType: "ORGANIC",
                                          })),
                                    );
                                    setInitialPromptGenerationError(null);
                                    toast.success("Prompts generated");
                              }
                              setPromptGenerationBroadcastStage(null);
                              clearInitialPromptGeneration();
                              return;
                        }

                        if (payload.stage === "FAILED") {
                              setPromptGenerationBroadcastStage(null);
                              clearInitialPromptGeneration();
                              setInitialPromptGenerationError(payload.error ?? "Failed to generate prompts.");
                              toast.error(payload.error ?? "Failed to generate prompts");
                        }
                        return;
                  }

                  const promptId = promptGenerationToPromptIdRef.current.get(payload.generationId);
                  if (!promptId) return;

                  if (payload.stage === "COMPLETED") {
                        const generatedPrompt = payload.prompts?.[0];
                        if (!generatedPrompt) {
                              toast.error("Prompt regeneration returned no result");
                        } else {
                              setPromptDrafts((prev) =>
                                    prev.map((prompt) =>
                                          prompt.id === promptId ? { ...prompt, text: generatedPrompt.text } : prompt,
                                    ),
                              );
                        }
                        clearRegenerationTracking(payload.generationId);
                        return;
                  }

                  if (payload.stage === "FAILED") {
                        clearRegenerationTracking(payload.generationId);
                        toast.error(payload.error ?? "Failed to regenerate prompt");
                  }
            },
      });

      const startInitialPromptGeneration = useCallback(async (): Promise<boolean> => {
            if (
                  !searchSpecificationId ||
                  initialPromptGenerationIdRef.current ||
                  generateOnboardingPromptsMutation.isPending
            ) {
                  return false;
            }

            if (!promptBroadcast.isSubscribed) {
                  const message = promptBroadcast.hasSubscriptionError
                        ? "Realtime connection is unavailable. Refresh and try again."
                        : "Waiting for realtime connection. Try again in a moment.";
                  setInitialPromptGenerationError(message);
                  toast.error(message);
                  return false;
            }

            const generationId = crypto.randomUUID();
            setHasTriggeredInitialGeneration(true);
            setInitialPromptGenerationError(null);
            initialPromptGenerationIdRef.current = generationId;
            setInitialPromptGenerationId(generationId);
            scheduleInitialPromptGenerationTimeout(generationId);

            try {
                  await generateOnboardingPromptsMutation.mutateAsync({
                        searchSpecificationId,
                        generationId,
                        count: ONBOARDING_STARTER_PROMPT_LIMIT,
                  });
                  return true;
            } catch (error) {
                  console.error("Failed to start prompt generation:", error);
                  clearInitialPromptGeneration();
                  setInitialPromptGenerationError("Failed to generate initial prompts.");
                  toast.error("Failed to generate initial prompts");
                  return false;
            }
      }, [
            clearInitialPromptGeneration,
            generateOnboardingPromptsMutation,
            promptBroadcast.hasSubscriptionError,
            promptBroadcast.isSubscribed,
            scheduleInitialPromptGenerationTimeout,
            searchSpecificationId,
      ]);

      useEffect(() => {
            if (!searchSpecificationId || isLoadingExistingPrompts || !hasFetchedExistingPrompts) return;

            if (existingPromptItems.length > 0) {
                  setPromptDrafts(
                        existingPromptItems.map((p) => ({
                              id: p.id,
                              text: p.text,
                              promptType: "ORGANIC",
                        })),
                  );
                  setInitialPromptGenerationError(null);
                  setHasTriggeredInitialGeneration(true);
            }
      }, [isLoadingExistingPrompts, hasFetchedExistingPrompts, existingPromptItems, searchSpecificationId]);

      useEffect(() => {
            if (
                  !shouldAutoStartInitialPromptGeneration({
                        searchSpecificationId,
                        hasTriggeredInitialGeneration,
                        hasFetchedExistingPrompts,
                        isLoadingExistingPrompts,
                        existingPromptCount: existingPromptItems.length,
                        isBroadcastSubscribed: promptBroadcast.isSubscribed,
                        isRequestPending: generateOnboardingPromptsMutation.isPending,
                        hasActiveGeneration: initialPromptGenerationId !== null,
                  })
            ) {
                  return;
            }

            void startInitialPromptGeneration();
      }, [
            existingPromptItems.length,
            generateOnboardingPromptsMutation.isPending,
            hasFetchedExistingPrompts,
            hasTriggeredInitialGeneration,
            initialPromptGenerationId,
            isLoadingExistingPrompts,
            promptBroadcast.isSubscribed,
            searchSpecificationId,
            startInitialPromptGeneration,
      ]);

      useEffect(() => {
            if (!promptBroadcast.hasSubscriptionError || existingPromptItems.length > 0 || hasTriggeredInitialGeneration) return;
            setInitialPromptGenerationError("Realtime connection is unavailable. Refresh and try again.");
      }, [existingPromptItems.length, hasTriggeredInitialGeneration, promptBroadcast.hasSubscriptionError]);

      const handleRegeneratePrompt = async (promptId: string) => {
            if (!searchSpecificationId || isSubmitting || initialPromptGenerationIdRef.current) return;
            if (!promptBroadcast.isSubscribed) {
                  const message = promptBroadcast.hasSubscriptionError
                        ? "Realtime connection is unavailable. Refresh and try again."
                        : "Waiting for realtime connection. Try again in a moment.";
                  toast.error(message);
                  return;
            }

            const generationId = crypto.randomUUID();
            setRegeneratingPromptIds((prev) => new Set(prev).add(promptId));
            promptGenerationToPromptIdRef.current.set(generationId, promptId);
            scheduleRegenerationTimeout(generationId, promptId);
            try {
                  await generateOnboardingPromptsMutation.mutateAsync({
                        searchSpecificationId,
                        generationId,
                        count: 1,
                  });
            } catch (error) {
                  console.error("Failed to regenerate onboarding prompt:", error);
                  toast.error("Failed to regenerate prompt. Please try again.");
                  clearRegenerationTracking(generationId);
            }
      };

      const handleStart = async () => {
            if (!brandBeingCreated?.slug || !searchSpecificationId) return;
            if (initialPromptGenerationIdRef.current) {
                  toast.error("Prompts are still generating. Please wait.");
                  return;
            }
            if (regeneratingPromptIds.size > 0) {
                  toast.error("Wait for prompt regeneration to finish.");
                  return;
            }
            if (promptDrafts.length === 0) {
                  toast.error("No prompts available. Regenerate prompts first.");
                  return;
            }

            setIsSubmitting(true);
            try {
                  if (existingPromptItems.length === 0) {
                        const promptsToCreate = promptDrafts.slice(0, ONBOARDING_STARTER_PROMPT_LIMIT);
                        await createPromptsMutation.mutateAsync({
                              prompts: promptsToCreate.map((prompt) => ({
                                    text: trimSafe(prompt.text),
                                    promptType: "ORGANIC" as const,
                                    searchSpecificationId,
                                    status: "ACTIVE" as const,
                              })),
                        });
                  }

                  await startSearchMutation.mutateAsync({ searchSpecificationId });

                  markBrandOnboardingCompleteInCache({
                        brandId: brandBeingCreated.id,
                        brandSearchActivityKey: trpc.brands.getBrandSearchActivity.queryKey({
                              brandId: brandBeingCreated.id,
                        }),
                        queryClient,
                        organizationHasSearchExecutionsKey: trpc.brands.hasAnyBrandWithSearchExecutions.queryKey({}),
                        brandSearchExecutionCountKey: trpc.brands.getBrandSearchExecutionsCount.queryKey({
                              brandId: brandBeingCreated.id,
                        }),
                        searchSpecificationId,
                  });

                  await queryClient.invalidateQueries({
                        queryKey: trpc.brands.getOrganizationBrands.queryKey({}),
                  });
                  await queryClient.invalidateQueries({
                        queryKey: trpc.searchSpecifications.getBrandSearchSpecifications.queryKey({
                              brandId: brandBeingCreated.id,
                        }),
                  });
                  await queryClient.invalidateQueries({
                        queryKey: trpc.brands.hasAnyBrandWithSearchExecutions.queryKey({}),
                  });
                  await queryClient.invalidateQueries({
                        queryKey: trpc.prompts.getSearchSpecificationPrompts.queryKey({ searchSpecificationId }),
                  });

                  setSelectedBrandId(brandBeingCreated.id);
                  setCurrentSearchSpecificationId(null);

                  navigate({
                        to: "/$brand/dashboard",
                        params: { brand: brandBeingCreated.slug },
                        replace: true,
                  });
            } catch (error) {
                  console.error("Failed onboarding prompt/start step:", error);
                  toast.error("Failed to start onboarding search. Please try again.");
            } finally {
                  setIsSubmitting(false);
            }
      };

      const handleBack = () => {
            navigate({
                  to: "/onboarding/brand/$brandId/persona",
                  params: { brandId },
            });
      };

      useEffect(
            () => () => {
                  if (initialPromptGenerationTimeoutRef.current !== null) {
                        window.clearTimeout(initialPromptGenerationTimeoutRef.current);
                  }
                  for (const timeoutId of promptGenerationTimeoutsRef.current.values()) {
                        window.clearTimeout(timeoutId);
                  }
            },
            [],
      );

      if (!sessionData) return null;
      if (isLoadingBrand || isLoadingBrandSearchExecutionCount) {
            return <OnboardingStepSkeleton fieldCount={5} showBackButton />;
      }
      if (!brandBeingCreated) return null;

      const initialPromptGenerationViewState = getInitialPromptGenerationViewState({
            searchSpecificationId,
            hasFetchedExistingPrompts,
            isLoadingExistingPrompts:
                  isLoadingExistingPrompts ||
                  (!!searchSpecificationId &&
                        existingPromptItems.length === 0 &&
                        !hasTriggeredInitialGeneration &&
                        !promptBroadcast.hasSubscriptionError),
            hasTriggeredInitialGeneration,
            initialPromptGenerationId,
            promptDraftCount: promptDrafts.length,
            initialPromptGenerationError,
            hasSubscriptionError: promptBroadcast.hasSubscriptionError,
      });
      const isInitialPromptGenerationPending = initialPromptGenerationViewState === "loading";
      const isPromptGenerationRequestPending = generateOnboardingPromptsMutation.isPending;
      const isPromptStepBusy = isSubmitting || createPromptsMutation.isPending || startSearchMutation.isPending;

      if (isInitialPromptGenerationPending) {
            return (
                  <GenerationLoadingState
                        title="Generating Prompts"
                        estimatedDuration="Usually about 30 seconds"
                        currentStageId={promptGenerationBroadcastStage}
                        stages={PROMPT_GENERATION_STAGES}
                        tips={PROMPT_TIPS}
                  />
            );
      }

      if (promptDrafts.length === 0) {
            const emptyStateMessage =
                  initialPromptGenerationViewState === "error"
                        ? (initialPromptGenerationError ?? "Realtime connection is unavailable. Refresh and try again.")
                        : "Generate your starting prompts to continue.";

            return (
                  <div className="w-full">
                        <OnboardingHeader
                              title="Generate Starting Prompts"
                              description={emptyStateMessage}
                              className="mb-6"
                        />

                        <div className="flex items-center justify-between pt-6">
                              <Button
                                    type="button"
                                    variant="brandOutline"
                                    onClick={handleBack}
                                    disabled={isPromptStepBusy || isPromptGenerationRequestPending}
                              >
                                    Back
                              </Button>

                              <Button
                                    type="button"
                                    variant="brand"
                                    onClick={() => void startInitialPromptGeneration()}
                                    disabled={isPromptStepBusy || isPromptGenerationRequestPending || !promptBroadcast.isSubscribed}
                                    className="px-8"
                              >
                                    <Sparkles className="mr-2 h-4 w-4" />
                                    Generate Prompts
                              </Button>
                        </div>
                  </div>
            );
      }

      return (
            <div className="w-full">
                  <OnboardingHeader
                        title="Review Generated Prompts"
                        description="Edit prompts or regenerate each one before starting your first search."
                        className="mb-6"
                  />

                  <div className="flex flex-col gap-3">
                        {promptDrafts.map((prompt, index) => {
                              const isRegenerating = regeneratingPromptIds.has(prompt.id);
                              const isRowDisabled =
                                    isPromptStepBusy ||
                                    isInitialPromptGenerationPending ||
                                    isPromptGenerationRequestPending ||
                                    isRegenerating;

                              return (
                                    <div key={prompt.id} className="flex items-center gap-3">
                                          <span className="w-6 shrink-0 text-xs font-semibold tabular-nums text-brand-ink/30">
                                                {index + 1}.
                                          </span>
                                          <Input
                                                {...getPromptEditorAutofillProps(`onboarding-generated-prompt-${index + 1}`)}
                                                value={prompt.text}
                                                onChange={(event) =>
                                                      setPromptDrafts((prev) =>
                                                            prev.map((draft) =>
                                                                  draft.id === prompt.id
                                                                        ? { ...draft, text: event.target.value }
                                                                        : draft,
                                                            ),
                                                      )
                                                }
                                                placeholder="Generated prompt..."
                                                variant="brand"
                                                disabled={isRowDisabled}
                                          />
                                          <Button
                                                type="button"
                                                variant="brandOutline"
                                                size="icon"
                                                className="shrink-0 text-brand-ink/40 hover:text-brand-ink"
                                                onClick={() => handleRegeneratePrompt(prompt.id)}
                                                disabled={isRowDisabled}
                                          >
                                                {isRegenerating ? (
                                                      <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                      <RefreshCw className="h-4 w-4" />
                                                )}
                                          </Button>
                                    </div>
                              );
                        })}

                        <div className="flex items-center justify-between pt-6">
                              <Button
                                    type="button"
                                    variant="brandOutline"
                                    onClick={handleBack}
                                    disabled={isPromptStepBusy}
                              >
                                    Back
                              </Button>

                              <LoadingButton
                                    variant="brand"
                                    onClick={handleStart}
                                    loading={isPromptStepBusy}
                                    loadingText="Starting..."
                                    disabled={
                                          isPromptStepBusy ||
                                          isInitialPromptGenerationPending ||
                                          isPromptGenerationRequestPending ||
                                          regeneratingPromptIds.size > 0 ||
                                          promptDrafts.length === 0 ||
                                          promptDrafts.some((prompt) => trimSafe(prompt.text).length === 0)
                                    }
                                    className="px-8"
                              >
                                    Start
                              </LoadingButton>
                        </div>
                  </div>
            </div>
      );
}
